import React, { Dispatch, SetStateAction } from 'react';
import {
  View, Text, StyleSheet, Pressable,
} from 'react-native';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import { AssetNote, UserPermission } from '../../../../../../../constants/Types';
import PressableStyled, { PressableState } from '../../../../../../../components/PressableStyled';
import EditIcon from '../../../../../../../components/EditIcon';
import Style from '../../../../../../../constants/Style';
import Colors from '../../../../../../../constants/Colors';
import isMobile from '../../../../../../../components/IsMobile';

interface NoteItemProps {
  note: AssetNote,
  testID?: string,
  editModalShown: AssetNote | null,
  setEditModalShown: Dispatch<SetStateAction<AssetNote | null>>,
  setNoteViewMode: Dispatch<SetStateAction<boolean>>,
}

const NoteItem = ({
  note, testID, editModalShown, setEditModalShown, setNoteViewMode,
}: NoteItemProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditAsset: boolean = isAdmin
    || permissions.includes(UserPermission.ASSETS_UPDATE);
  const timestamp: string = Moment(note.updatedOn ?? note.createdOn)
    .format('MMM D, YYYY[, at ]h:mm A');

  const onEditPress: () => void = () => {
    setEditModalShown(note);
    setNoteViewMode(false);
  };

  return <View style={styles.wrapper}>
    <View style={Style.row}>
      <Text style={styles.description}>
        {`${note.name} ${note.updatedOn ? 'updated' : 'added'} a note:`}
      </Text>
      {!isMobile && <Text style={styles.description}>{timestamp}</Text>}
    </View>
    <Pressable
      onPress={() => {
        if (editModalShown) return;
        setEditModalShown(note);
        setNoteViewMode(true);
      }}
      style={({ hovered, pressed }: PressableState) => [
        styles.container, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
      ]}
      disabled={isMobile}
      {...{ testID }}
    >
      <View style={{ flex: 1 }}>
        <Text style={styles.title}>{note.title}</Text>
        <View style={styles.noteContent}>
          <Text style={styles.description}>{note.description?.trim() || '-'}</Text>
          {canEditAsset && <PressableStyled onPress={onEditPress}>
            <EditIcon />
          </PressableStyled>}
        </View>
      </View>
    </Pressable>
    {isMobile && <Text style={styles.description}>{timestamp}</Text>}
  </View>;
};

const styles = StyleSheet.create({
  wrapper: {
    marginBottom: isMobile ? 16 : 12,
    marginHorizontal: isMobile ? undefined : 12,
  },
  container: {
    paddingVertical: 12,
    paddingHorizontal: isMobile ? 16 : 12,
    borderRadius: isMobile ? 10 : 6,
    backgroundColor: Colors.light.hereworksWhite100,
    marginTop: 8,
  },
  title: {
    fontSize: 14,
    lineHeight: isMobile ? undefined : 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    letterSpacing: 0,
    marginBottom: 4,
  },
  noteContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  description: {
    fontSize: 14,
    lineHeight: isMobile ? undefined : 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginTop: isMobile ? 4 : 8,
    letterSpacing: 0,
    marginEnd: 16,
  },
  timestamp: {
    fontSize: 10,
    lineHeight: 16,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
    letterSpacing: 0.1,
    marginTop: isMobile ? 4 : 16,
    marginBottom: isMobile ? 8 : 4,
  },
});

export default NoteItem;
